import * as THREE from "three"

// Shared equirectangular (360°) viewer.
//
// A 360 photo or video frame is stored as a 2:1 equirectangular projection, which looks warped
// when shown flat. We map it onto an inside-out sphere and put the camera at the centre, so the
// user drags to look around instead of squinting at a distorted rectangle.
//
// Two callers, deliberately one implementation: the beam teleop panel feeds a live
// THREE.VideoTexture from the dock's Ricoh Theta, and the PhotoSwipe gallery feeds a still
// THREE.Texture loaded from a document/Baufortschritt image.

const SPHERE_RADIUS = 500
const DEFAULT_FOV = 75
const MIN_FOV = 30
const MAX_FOV = 100
const DRAG_SENSITIVITY = 0.1
const MAX_LATITUDE = 85
const AUTO_ROTATE_DEGREES_PER_FRAME = 0.02
// A device pixel ratio above 2 buys nothing visible here but quadruples the framebuffer.
const MAX_PIXEL_RATIO = 2

// Loads an image URL into a texture ready for the sphere. Rejects if the image can't be
// fetched, so callers can fall back to showing the flat image.
export function loadPanoramaTexture(src) {
  return new Promise((resolve, reject) => {
    const loader = new THREE.TextureLoader()
    loader.setCrossOrigin("anonymous")
    loader.load(
      src,
      (texture) => {
        texture.colorSpace = THREE.SRGBColorSpace
        resolve(texture)
      },
      undefined,
      reject,
    )
  })
}

// Mounts a viewer into `mount` and returns a handle with a single `destroy()`.
//
// Options:
//   texture       — THREE.Texture or THREE.VideoTexture to wrap on the sphere (required)
//   autoRotate    — drift slowly until the first interaction, hinting that the image is draggable
//   captureEvents — stop pointer/wheel events from bubbling. Required inside PhotoSwipe, whose
//                   own swipe handler would otherwise steal the drag and flip to the next slide.
//   ownsTexture   — dispose the texture on destroy (default true; false when the caller reuses it)
export function createPanoramaViewer(
  mount,
  { texture, autoRotate = false, captureEvents = false, ownsTexture = true } = {},
) {
  const width = mount.clientWidth || 640
  const height = mount.clientHeight || 320

  const scene = new THREE.Scene()
  const camera = new THREE.PerspectiveCamera(DEFAULT_FOV, width / height, 0.1, SPHERE_RADIUS * 2.2)
  const geometry = new THREE.SphereGeometry(SPHERE_RADIUS, 60, 40)
  geometry.scale(-1, 1, 1) // view from inside the sphere
  const material = new THREE.MeshBasicMaterial({ map: texture })
  scene.add(new THREE.Mesh(geometry, material))

  const renderer = new THREE.WebGLRenderer({ antialias: true })
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, MAX_PIXEL_RATIO))
  renderer.setSize(width, height)
  const canvas = renderer.domElement
  canvas.style.touchAction = "none" // let us handle drag/pinch instead of the browser panning
  canvas.style.cursor = "grab"
  mount.appendChild(canvas)

  const view = { lon: 0, lat: 0, fov: DEFAULT_FOV, dragging: false, px: 0, py: 0, autoRotate }
  // Tracked so a two-finger gesture can be told apart from a one-finger drag.
  const pointers = new Map()
  let pinchDistance = 0

  const stop = (event) => {
    if (captureEvents) event.stopPropagation()
  }

  const applyFov = (next) => {
    view.fov = Math.max(MIN_FOV, Math.min(MAX_FOV, next))
    camera.fov = view.fov
    camera.updateProjectionMatrix()
  }

  const pinchSpan = () => {
    const [a, b] = [...pointers.values()]
    return Math.hypot(a.x - b.x, a.y - b.y)
  }

  const onPointerDown = (event) => {
    stop(event)
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY })
    view.autoRotate = false
    if (pointers.size === 2) {
      pinchDistance = pinchSpan()
      view.dragging = false
      return
    }
    view.dragging = true
    view.px = event.clientX
    view.py = event.clientY
    canvas.style.cursor = "grabbing"
    // Pointer capture keeps move/up on the canvas even when the drag leaves it, so we never
    // need window-level listeners (and can't leak them).
    if (canvas.setPointerCapture) canvas.setPointerCapture(event.pointerId)
  }

  const onPointerMove = (event) => {
    if (!pointers.has(event.pointerId)) return
    stop(event)
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY })

    if (pointers.size === 2) {
      const span = pinchSpan()
      if (pinchDistance > 0) applyFov(view.fov * (pinchDistance / span))
      pinchDistance = span
      return
    }
    if (!view.dragging) return

    view.lon -= (event.clientX - view.px) * DRAG_SENSITIVITY
    view.lat += (event.clientY - view.py) * DRAG_SENSITIVITY
    view.lat = Math.max(-MAX_LATITUDE, Math.min(MAX_LATITUDE, view.lat))
    view.px = event.clientX
    view.py = event.clientY
  }

  const onPointerUp = (event) => {
    if (!pointers.has(event.pointerId)) return
    stop(event)
    pointers.delete(event.pointerId)
    pinchDistance = 0
    view.dragging = false
    canvas.style.cursor = "grab"
    if (canvas.releasePointerCapture && canvas.hasPointerCapture?.(event.pointerId)) {
      canvas.releasePointerCapture(event.pointerId)
    }
  }

  const onWheel = (event) => {
    event.preventDefault()
    stop(event)
    view.autoRotate = false
    applyFov(view.fov + event.deltaY * 0.05)
  }

  canvas.addEventListener("pointerdown", onPointerDown)
  canvas.addEventListener("pointermove", onPointerMove)
  canvas.addEventListener("pointerup", onPointerUp)
  canvas.addEventListener("pointercancel", onPointerUp)
  canvas.addEventListener("wheel", onWheel, { passive: false })

  const resize = () => {
    const w = mount.clientWidth
    const h = mount.clientHeight
    if (!w || !h) return
    camera.aspect = w / h
    camera.updateProjectionMatrix()
    renderer.setSize(w, h)
  }
  // A ResizeObserver also catches the mount growing inside a lightbox, which a window resize
  // listener would miss.
  const observer = new ResizeObserver(resize)
  observer.observe(mount)

  let frame = 0
  const animate = () => {
    frame = requestAnimationFrame(animate)
    if (view.autoRotate) view.lon += AUTO_ROTATE_DEGREES_PER_FRAME
    const phi = THREE.MathUtils.degToRad(90 - view.lat)
    const theta = THREE.MathUtils.degToRad(view.lon)
    camera.lookAt(
      SPHERE_RADIUS * Math.sin(phi) * Math.cos(theta),
      SPHERE_RADIUS * Math.cos(phi),
      SPHERE_RADIUS * Math.sin(phi) * Math.sin(theta),
    )
    renderer.render(scene, camera)
  }
  animate()

  return {
    resize,
    destroy() {
      cancelAnimationFrame(frame)
      observer.disconnect()
      canvas.removeEventListener("pointerdown", onPointerDown)
      canvas.removeEventListener("pointermove", onPointerMove)
      canvas.removeEventListener("pointerup", onPointerUp)
      canvas.removeEventListener("pointercancel", onPointerUp)
      canvas.removeEventListener("wheel", onWheel)
      if (ownsTexture) texture.dispose()
      geometry.dispose()
      material.dispose()
      // Frees the WebGL context. Browsers cap these at ~16, so skipping it means the gallery
      // stops rendering after a dozen or so 360 slides.
      renderer.dispose()
      renderer.forceContextLoss?.()
      canvas.remove()
    },
  }
}
